import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { sentencesAPI } from './api';

const BASE_URL = process.env.REACT_APP_API_URL || '';

const getMastered = async (category, token) => {
  try {
    const res = await fetch(`${BASE_URL}/api/progress/cumulative/${category}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    return data.manualMastered || {};
  } catch {
    return {};
  }
};

// 句子音频路径拼接（服务器返回的是相对路径，需要补全域名；如果已经是完整 URL 则不处理）
const resolveSentenceAudioUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://www.xiaocixia.cn${url}`;
};

function WordPlayer({ isOpen, onClose, words, filteredWords, category, categories, theme, sentenceSources, defaultSentenceSource, initialContentType }) {
  // ===== 内容类型：单词 / 句子 =====
  const [contentType, setContentType] = useState(initialContentType === 'sentence' ? 'sentence' : 'word'); // 'word' | 'sentence'

  const [playerCategory, setPlayerCategory] = useState(category);
  const [playerSource, setPlayerSource] = useState(defaultSentenceSource || '');
  const [playScope, setPlayScope] = useState('all');
  const [playMode, setPlayMode] = useState('en'); // 'en' | 'bilingual'（句子模式下没有中文语音，固定为 'en'）
  const playModeRef = useRef('en');
  const isStoppedRef = useRef(false);
  const playTaskIdRef = useRef(0);
  useEffect(() => { playModeRef.current = playMode; }, [playMode]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greenScreenMode, setGreenScreenMode] = useState(false);
  const [greenScreenOpacity, setGreenScreenOpacity] = useState(50);
  const [sortMode, setSortMode] = useState('asc'); // 单词: 'asc' | 'desc' | 'random'；句子: 'length' | 'random'
  const [startLetter, setStartLetter] = useState(''); // 起始字母，空表示从头开始（仅单词模式）
  const [pronunciationType, setPronunciationType] = useState('us');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loopMode, setLoopMode] = useState('none');
  const [audioError, setAudioError] = useState(false);
  const [masteredMap, setMasteredMap] = useState({});
  const [isLoadingMastered, setIsLoadingMastered] = useState(false);
  const [apiWords, setApiWords] = useState([]);
  const [apiSentences, setApiSentences] = useState([]);
  const [isLoadingSentences, setIsLoadingSentences] = useState(false);

  // 新功能状态
  const [fontSize, setFontSize] = useState(1);
  const [repeatCount, setRepeatCount] = useState(1);
  const [intervalSec, setIntervalSec] = useState(0.6);
  const [timerMinutes, setTimerMinutes] = useState(0);      // 0 = 不限
  const [timerRemaining, setTimerRemaining] = useState(0);  // 剩余秒数

  const audioRef = useRef(null);
  const autoPlayTimerRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentIndexRef = useRef(0);
  const pronunciationRef = useRef('us');
  const loopModeRef = useRef('none');
  const repeatCountRef = useRef(1);
  const intervalSecRef = useRef(0.6);
  const contentTypeRef = useRef('word');

  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { pronunciationRef.current = pronunciationType; }, [pronunciationType]);
  useEffect(() => { loopModeRef.current = loopMode; }, [loopMode]);
  useEffect(() => { repeatCountRef.current = repeatCount; }, [repeatCount]);
  useEffect(() => { intervalSecRef.current = intervalSec; }, [intervalSec]);
  useEffect(() => { contentTypeRef.current = contentType; }, [contentType]);

  // ===== 定时器逻辑 =====
  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback((minutes) => {
    stopTimer();
    if (minutes <= 0) {
      setTimerRemaining(0);
      return;
    }
    let remaining = minutes * 60;
    setTimerRemaining(remaining);
    timerIntervalRef.current = setInterval(() => {
      remaining -= 1;
      setTimerRemaining(remaining);
      if (remaining <= 0) {
        stopTimer();
        setTimerRemaining(0);
        setIsPlaying(false);
        // stopAudio 在下面定义，用 ref 调用
        stopAudioRef.current?.();
      }
    }, 1000);
  }, [stopTimer]);

  const handleTimerChange = useCallback((minutes) => {
    setTimerMinutes(minutes);
    if (minutes > 0) {
      startTimer(minutes);
    } else {
      stopTimer();
      setTimerRemaining(0);
    }
  }, [startTimer, stopTimer]);

  const formatRemaining = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  // ===== 拖动逻辑 =====
  const modalRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const posRef = useRef({ left: null, top: null });

  const handleDragStart = useCallback((e) => {
    const modal = modalRef.current;
    if (!modal) return;
    const rect = modal.getBoundingClientRect();
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };
    modal.style.left = rect.left + 'px';
    modal.style.top = rect.top + 'px';
    modal.style.bottom = 'auto';
    modal.style.transform = 'none';
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState.current.dragging) return;
      const modal = modalRef.current;
      if (!modal) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      let newLeft = dragState.current.startLeft + dx;
      let newTop = dragState.current.startTop + dy;
      newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - modal.offsetWidth));
      newTop = Math.max(0, Math.min(newTop, window.innerHeight - modal.offsetHeight));
      modal.style.left = newLeft + 'px';
      modal.style.top = newTop + 'px';
      posRef.current = { left: newLeft, top: newTop };
    };
    const onUp = () => { dragState.current.dragging = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const modal = modalRef.current;
      if (posRef.current.left !== null) {
        modal.style.left = posRef.current.left + 'px';
        modal.style.top = posRef.current.top + 'px';
        modal.style.bottom = 'auto';
        modal.style.transform = 'none';
      } else {
        modal.style.left = '';
        modal.style.top = '';
        modal.style.bottom = '';
        modal.style.transform = '';
      }
    }
  }, [isOpen]);

  // 实时更新日期时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 格式化日期时间显示
  const formatDateTime = useCallback((date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${month}-${day} ${hours}:${minutes}:${seconds}`;
  }, []);

  // ===== 获取单词掌握数据 =====
  const fetchMastered = useCallback(async (cat) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setIsLoadingMastered(true);
    const mastered = await getMastered(cat, token);
    setMasteredMap(mastered);
    setIsLoadingMastered(false);
  }, []);

  const fetchCategoryWords = useCallback(async (cat) => {
    try {
      const res = await fetch(`${BASE_URL}/api/words/category/${cat}`);
      const data = await res.json();
      setApiWords(data.words || []);
    } catch(e) {
      console.error('播放器加载单词失败:', e);
      setApiWords([]);
    }
  }, []);

  useEffect(() => { if (contentType === 'word') fetchCategoryWords(playerCategory); }, [playerCategory, contentType, fetchCategoryWords]);

  useEffect(() => {
    if (contentType === 'word' && playScope === 'unmastered') {
      setMasteredMap({});
      fetchMastered(playerCategory);
    }
  }, [playerCategory, playScope, contentType, fetchMastered]);

  // ===== 获取句子列表 / 句子掌握数据 =====
  const fetchSourceSentences = useCallback(async (source) => {
    if (!source) { setApiSentences([]); return; }
    setIsLoadingSentences(true);
    try {
      const data = await sentencesAPI.getBySource(source);
      setApiSentences(data.sentences || []);
    } catch (e) {
      console.error('播放器加载句子失败:', e);
      setApiSentences([]);
    }
    setIsLoadingSentences(false);
  }, []);

  const fetchMasteredSentences = useCallback(async (source) => {
    const token = localStorage.getItem('authToken');
    if (!token || !source) return;
    setIsLoadingMastered(true);
    try {
      const progress = await sentencesAPI.getProgress(source);
      setMasteredMap(progress.mastered || {});
    } catch {
      setMasteredMap({});
    }
    setIsLoadingMastered(false);
  }, []);

  // 句子来源列表：不依赖主页面是否处于“学句子”模式，播放器自己独立请求
  const [sourcesList, setSourcesList] = useState(sentenceSources || []);
  const fetchSentenceSources = useCallback(async () => {
    try {
      const data = await sentencesAPI.getSources();
      setSourcesList(data.sources || []);
    } catch (e) {
      console.error('播放器加载句子来源失败:', e);
    }
  }, []);

  useEffect(() => {
    if (isOpen && contentType === 'sentence' && sourcesList.length === 0) {
      fetchSentenceSources();
    }
  }, [isOpen, contentType, sourcesList.length, fetchSentenceSources]);

  useEffect(() => { if (contentType === 'sentence') fetchSourceSentences(playerSource); }, [playerSource, contentType, fetchSourceSentences]);

  useEffect(() => {
    if (contentType === 'sentence' && playScope === 'unmastered') {
      setMasteredMap({});
      fetchMasteredSentences(playerSource);
    }
  }, [playerSource, playScope, contentType, fetchMasteredSentences]);

  // 默认句子来源：首次拿到来源列表时，如果还没选过，选第一个
  useEffect(() => {
    if (!playerSource && sourcesList && sourcesList.length > 0) {
      setPlayerSource(sourcesList[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesList]);

  // ===== 当前播放列表（单词）=====
  const wordPlaylist = useMemo(() => {
    const sourceWords = apiWords.length > 0 ? apiWords : words.filter((w) => {
      if (Array.isArray(w.category)) {
        return w.category.some(
          (c) => String(c).toLowerCase().trim() === playerCategory.toLowerCase().trim()
        );
      }
      return String(w.category || '').toLowerCase().trim() === playerCategory.toLowerCase().trim();
    });
    let result = sourceWords;
    if (playScope === 'unmastered') {
      if (Object.keys(masteredMap).length === 0 && isLoadingMastered) return [];
      result = sourceWords.filter(w =>
        !masteredMap[w.word] && !masteredMap[w.word.toLowerCase()]
      );
    }

    // 应用排序模式
    if (sortMode === 'desc') {
      result = [...result].sort((a, b) => b.word.toLowerCase().localeCompare(a.word.toLowerCase()));
    } else if (sortMode === 'random') {
      result = [...result].sort(() => Math.random() - 0.5);
    } else {
      // asc：保持原有的字母顺序（默认就是A-Z）
      result = [...result].sort((a, b) => a.word.toLowerCase().localeCompare(b.word.toLowerCase()));
    }

    // 应用起始字母筛选（只在顺序/倒序模式下生效，随机模式不适用）
    if (startLetter && sortMode !== 'random') {
      const startIdx = sortMode === 'desc'
        ? result.findIndex(w => w.word.toLowerCase().charAt(0) <= startLetter.toLowerCase())
        : result.findIndex(w => w.word.toLowerCase().charAt(0) >= startLetter.toLowerCase());
      if (startIdx > 0) {
        result = [...result.slice(startIdx), ...result.slice(0, startIdx)];
      }
    }

    return result;
  }, [apiWords, words, playerCategory, playScope, masteredMap, isLoadingMastered, sortMode, startLetter]);

  // ===== 当前播放列表（句子）=====
  const sentencePlaylist = useMemo(() => {
    let result = apiSentences;
    if (playScope === 'unmastered') {
      if (Object.keys(masteredMap).length === 0 && isLoadingMastered) return [];
      result = result.filter(s => !masteredMap[s.id]);
    }

    if (sortMode === 'random') {
      result = [...result].sort(() => Math.random() - 0.5);
    } else {
      // 句子模式下的“顺序”＝按句子长度从短到长（原始添加顺序无实际学习意义）
      result = [...result].sort((a, b) => (a.sentence_en?.length || 0) - (b.sentence_en?.length || 0));
    }

    return result;
  }, [apiSentences, playScope, masteredMap, isLoadingMastered, sortMode]);

  const playlist = contentType === 'word' ? wordPlaylist : sentencePlaylist;

  const playlistRef = useRef(playlist);
  useEffect(() => { playlistRef.current = playlist; }, [playlist]);

  const currentWord = playlist[currentIndex] || null; // 兼容旧命名：单词模式下是单词，句子模式下是句子

  const shortMeaning = useMemo(() => {
    if (contentType === 'sentence') return currentWord?.sentence_cn || '';
    if (!currentWord?.meaning_cn) return '';
    return currentWord.meaning_cn.split('；').slice(0, 2).join('；');
  }, [currentWord, contentType]);

  // ===== 停止音频 =====
  const stopAudio = useCallback(() => {
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    isStoppedRef.current = true;
  }, []);

  // 用 ref 暴露给定时器回调
  const stopAudioRef = useRef(stopAudio);
  useEffect(() => { stopAudioRef.current = stopAudio; }, [stopAudio]);

  // ===== 进入下一个 =====
  const advanceToNext = useCallback((currentIdx, list, pronType) => {
    const nextIndex = currentIdx + 1;
    if (nextIndex >= list.length) {
      if (loopModeRef.current === 'all') {
        playWordFn(0, list, pronType);
      } else {
        setIsPlaying(false);
      }
    } else {
      playWordFn(nextIndex, list, pronType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== 播放指定条目（单词或句子，由 contentTypeRef 决定）=====
  const playWordFn = useCallback((index, list, pronType) => {
    stopAudio();
    isStoppedRef.current = false;
    playTaskIdRef.current += 1;
    const myTaskId = playTaskIdRef.current;
    setAudioError(false);
    if (!list || list.length === 0) { setIsPlaying(false); return; }
    const safeIndex = index >= list.length ? 0 : index < 0 ? list.length - 1 : index;
    const item = list[safeIndex];
    if (!item) { setIsPlaying(false); return; }
    setCurrentIndex(safeIndex);
    currentIndexRef.current = safeIndex;

    const isSentence = contentTypeRef.current === 'sentence';
    const audioPath = isSentence
      ? resolveSentenceAudioUrl(pronType === 'uk' ? item.audio_uk : item.audio_us)
      : item.audio?.[pronType];

    if (!audioPath) {
      setAudioError(true);
      autoPlayTimerRef.current = setTimeout(() => {
        if (myTaskId !== playTaskIdRef.current) return;
        advanceToNext(safeIndex, list, pronType);
      }, 800);
      return;
    }

    const handleAfterWordEnded = (repeatsDone) => {
      if (myTaskId !== playTaskIdRef.current || isStoppedRef.current) return;
      if (loopModeRef.current === 'one') {
        autoPlayTimerRef.current = setTimeout(() => {
          if (myTaskId !== playTaskIdRef.current) return;
          playOnce(0);
        }, intervalSecRef.current * 1000);
        return;
      }
      if (repeatsDone + 1 < repeatCountRef.current) {
        autoPlayTimerRef.current = setTimeout(() => {
          if (myTaskId !== playTaskIdRef.current) return;
          playOnce(repeatsDone + 1);
        }, intervalSecRef.current * 1000);
      } else {
        autoPlayTimerRef.current = setTimeout(() => {
          if (myTaskId !== playTaskIdRef.current) return;
          advanceToNext(safeIndex, playlistRef.current, pronunciationRef.current);
        }, intervalSecRef.current * 1000);
      }
    };

    const playOnce = (repeatsDone) => {
      if (myTaskId !== playTaskIdRef.current) return;
      const audio = new Audio(audioPath);
      audioRef.current = audio;
      audio.play().catch(() => setAudioError(true));
      audio.addEventListener('ended', () => {
        if (myTaskId !== playTaskIdRef.current || isStoppedRef.current) return;
        // 双语模式：单词播中文释义语音，句子播中文语音（现在句子也有 audio_cn 了）
        const cnAudioPath = isSentence
          ? resolveSentenceAudioUrl(item.audio_cn)
          : (item.meaning_audio_cn ? `https://www.xiaocixia.cn${item.meaning_audio_cn}` : null);
        console.log('[WordPlayer] 英文播放结束，检查双语', {
          playMode: playModeRef.current,
          isSentence,
          rawAudioCn: item.audio_cn,
          cnAudioPath,
        });
        if (playModeRef.current === 'bilingual' && cnAudioPath) {
          console.log('[WordPlayer] 开始播放中文音频:', cnAudioPath);
          const cnAudio = new Audio(cnAudioPath);
          audioRef.current = cnAudio;
          cnAudio.play()
            .then(() => console.log('[WordPlayer] 中文音频 play() 成功触发'))
            .catch((err) => {
              console.error('[WordPlayer] 中文音频播放失败:', err);
              // 播放失败也要继续往下走，不然会卡住不动
              if (myTaskId !== playTaskIdRef.current || isStoppedRef.current) return;
              handleAfterWordEnded(repeatsDone);
            });
          cnAudio.addEventListener('ended', () => {
            console.log('[WordPlayer] 中文音频播放结束');
            if (myTaskId !== playTaskIdRef.current || isStoppedRef.current) return;
            handleAfterWordEnded(repeatsDone);
          }, { once: true });
          cnAudio.addEventListener('error', (e) => {
            console.error('[WordPlayer] 中文音频 error 事件:', e);
            if (myTaskId !== playTaskIdRef.current || isStoppedRef.current) return;
            handleAfterWordEnded(repeatsDone);
          }, { once: true });
        } else {
          handleAfterWordEnded(repeatsDone);
        }
      }, { once: true });
    };

    playOnce(0);
  }, [stopAudio, advanceToNext]);

  // ===== 播放/暂停 =====
  const handlePlayPause = useCallback(() => {
    if (playlist.length === 0) return;
    if (isPlaying) {
      stopAudio();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      playWordFn(currentIndex, playlist, pronunciationType);
    }
  }, [isPlaying, playlist, currentIndex, pronunciationType, stopAudio, playWordFn]);

  const handlePrev = useCallback(() => {
    if (playlist.length === 0) return;
    stopAudio();
    const newIndex = currentIndex > 0 ? currentIndex - 1 : playlist.length - 1;
    setCurrentIndex(newIndex);
    if (isPlaying) playWordFn(newIndex, playlist, pronunciationType);
  }, [currentIndex, playlist, isPlaying, pronunciationType, stopAudio, playWordFn]);

  const handleNext = useCallback(() => {
    if (playlist.length === 0) return;
    stopAudio();
    const newIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    if (isPlaying) playWordFn(newIndex, playlist, pronunciationType);
  }, [currentIndex, playlist, isPlaying, pronunciationType, stopAudio, playWordFn]);

  const handleCategoryChange = useCallback((newCat) => {
    stopAudio(); setIsPlaying(false); setCurrentIndex(0); setPlayerCategory(newCat);
  }, [stopAudio]);

  const handleSourceChange = useCallback((newSource) => {
    stopAudio(); setIsPlaying(false); setCurrentIndex(0); setPlayerSource(newSource);
  }, [stopAudio]);

  // ===== 内容类型切换（单词 ⇄ 句子）=====
  const handleContentTypeChange = useCallback((type) => {
    if (type === contentType) return;
    stopAudio();
    setIsPlaying(false);
    setCurrentIndex(0);
    setAudioError(false);
    setPlayScope('all');
    setMasteredMap({});
    // 句子模式下选“双语”不会有额外效果（没有中文语音），但选项本身照常保留、不强制重置
    if (type === 'sentence') {
      setSortMode('length');
      setStartLetter('');
    } else {
      setSortMode('asc');
    }
    setContentType(type);
  }, [contentType, stopAudio]);

  useEffect(() => {
    if (isLoadingMastered) return;
    stopAudio();
    setCurrentIndex(0);
    if (playlist.length > 0 && isPlayingRef.current) {
      setTimeout(() => { playWordFn(0, playlist, pronunciationRef.current); }, 100);
    } else if (playlist.length === 0) {
      setIsPlaying(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerCategory, playerSource, playScope, isLoadingMastered, contentType]);

  const handleScopeChange = useCallback((scope) => {
    stopAudio(); setIsPlaying(false); setCurrentIndex(0); setPlayScope(scope);
  }, [stopAudio]);

  const handlePronChange = useCallback((type) => {
    setPronunciationType(type);
    pronunciationRef.current = type;
    if (isPlaying) {
      stopAudio();
      setTimeout(() => { playWordFn(currentIndexRef.current, playlistRef.current, type); }, 100);
    }
  }, [isPlaying, stopAudio, playWordFn]);

  const handleLoopToggle = useCallback(() => {
    setLoopMode((prev) => {
      const next = prev === 'none' ? 'all' : prev === 'all' ? 'one' : 'none';
      loopModeRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) { stopAudio(); setIsPlaying(false); stopTimer(); }
  }, [isOpen, stopAudio, stopTimer]);

  useEffect(() => { setPlayerCategory(category); }, [category]);
  useEffect(() => { if (isOpen && contentType === 'word') fetchCategoryWords(playerCategory); }, [isOpen, playerCategory, contentType, fetchCategoryWords]);
  useEffect(() => { if (isOpen && contentType === 'sentence') fetchSourceSentences(playerSource); }, [isOpen, playerSource, contentType, fetchSourceSentences]);
  useEffect(() => { return () => { stopAudio(); stopTimer(); }; }, [stopAudio, stopTimer]);

  if (!isOpen) return null;

  const isDark = theme === 'dark';
  const isSentenceMode = contentType === 'sentence';
  const loopIcon = loopMode === 'one' ? '🔂' : loopMode === 'all' ? '🔁' : '➡️';
  const loopTitle = loopMode === 'one' ? (isSentenceMode ? '单句循环' : '单词循环') : loopMode === 'all' ? '列表循环' : '顺序播放';

  const fontSizeOptions = [
    { value: 1, label: '标准' },
    { value: 1.5, label: '大' },
    { value: 2, label: '超大' },
  ];

  const timerOptions = [
    { value: 0, label: '不限' },
    { value: 30, label: '30分' },
    { value: 60, label: '1小时' },
    { value: 120, label: '2小时' },
    { value: 180, label: '3小时' },
  ];

  const isLoadingList = isSentenceMode ? isLoadingSentences : false;

  return (
    <div ref={modalRef} className={`wp-modal ${isDark ? 'wp-dark' : 'wp-light'} ${greenScreenMode ? 'wp-green-screen' : ''}`}>
      {greenScreenMode && (
        <div 
          className="wp-green-overlay" 
          style={{ opacity: greenScreenOpacity / 100 }}
        />
      )}

      {/* 顶栏：拖动手柄 + 单词/句子切换 + 绿屏 + 时间 + 关闭 */}
      <div className="wp-topbar">
        <div className="wp-drag-handle" onMouseDown={handleDragStart}>
          <div className="wp-drag-dots">
            {[...Array(6)].map((_, i) => <span key={i} />)}
          </div>
          <span>拖动移动</span>
        </div>
        <div className="wp-scope-btns" style={{ margin: 0 }}>
          <button
            className={`wp-scope-btn ${contentType === 'word' ? 'wp-scope-active' : ''}`}
            onClick={() => handleContentTypeChange('word')}
          >📝 单词</button>
          <button
            className={`wp-scope-btn ${contentType === 'sentence' ? 'wp-scope-active' : ''}`}
            onClick={() => handleContentTypeChange('sentence')}
          >📖 句子</button>
        </div>
        <button 
          className={`wp-green-btn ${greenScreenMode ? 'wp-green-active' : ''}`}
          onClick={() => setGreenScreenMode(!greenScreenMode)}
          title="绿屏直播模式"
        >
          🟢
        </button>
        <span className="wp-datetime">{formatDateTime(currentTime)}</span>
        <div className="wp-topbar-right">
          {timerMinutes > 0 && timerRemaining > 0 && (
            <span className="wp-timer-badge">
              ⏱ {formatRemaining(timerRemaining)}
            </span>
          )}
          {timerMinutes > 0 && timerRemaining === 0 && (
            <span className="wp-timer-badge wp-timer-done">⏱ 时间到</span>
          )}
          <button className="wp-close" onClick={onClose} title="关闭">✕</button>
        </div>
      </div>
      {greenScreenMode && (
        <div className="wp-green-slider-row">
          <span style={{ fontSize: '0.75em' }}>透明度</span>
          <input
            type="range"
            min="10"
            max="90"
            value={greenScreenOpacity}
            onChange={(e) => setGreenScreenOpacity(parseInt(e.target.value))}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: '0.75em', minWidth: '32px' }}>{greenScreenOpacity}%</span>
        </div>
      )}

      {/* 单词/句子显示区 */}
      <div className="wp-display" style={{ position: 'relative' }}>
        <span className="wp-counter-corner">
          {playlist.length > 0 ? `${currentIndex + 1} / ${playlist.length}` : '0 / 0'}
        </span>
        {isLoadingMastered || isLoadingList ? (
          <div className="wp-empty">加载中...</div>
        ) : playlist.length === 0 ? (
          <div className="wp-empty">{isSentenceMode ? '该来源暂无句子' : '该范围暂无单词'}</div>
        ) : isSentenceMode ? (
          <>
            <div className="wp-word" style={{ fontSize: `${fontSize * 1.3}em`, fontStyle: 'italic', lineHeight: 1.5 }}>
              {currentWord?.sentence_en || ''}
              {audioError && <span className="wp-no-audio">（无音频）</span>}
            </div>
            <div className="wp-meaning" style={{ fontSize: `${fontSize * 0.95}em` }}>
              {shortMeaning}
            </div>
          </>
        ) : (
          <>
            <div className="wp-word" style={{ fontSize: `${fontSize * 2.4}em` }}>
              {currentWord?.word || ''}
            </div>
            <div className="wp-phonetic" style={{ fontSize: `${fontSize * 1}em` }}>
              {currentWord?.phonetic?.[pronunciationType] || ''}
              {audioError && <span className="wp-no-audio">（无音频）</span>}
            </div>
            <div className="wp-meaning" style={{ fontSize: `${fontSize * 0.95}em` }}>
              {shortMeaning}
            </div>
          </>
        )}
      </div>

      <div className="wp-divider" />

      {/* 播放控制 */}
      <div className="wp-playbar">
        <div className="wp-playbar-left">
          {/* 级别 / 来源 选择 */}
          {isSentenceMode ? (
            <select value={playerSource} onChange={(e) => handleSourceChange(e.target.value)} className="wp-cat-select">
              {(sourcesList || []).map((src) => (
                <option key={src} value={src}>{src}</option>
              ))}
            </select>
          ) : (
            <select value={playerCategory} onChange={(e) => handleCategoryChange(e.target.value)} className="wp-cat-select">
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label.replace(/[（(][^）)]*[）)]/g, '').trim()}</option>
              ))}
            </select>
          )}
          <div className="wp-scope-btns">
            <button className={`wp-scope-btn ${playScope === 'all' ? 'wp-scope-active' : ''}`} onClick={() => handleScopeChange('all')}>全部</button>
            <button className={`wp-scope-btn ${playScope === 'unmastered' ? 'wp-scope-active' : ''}`} onClick={() => handleScopeChange('unmastered')}>{isSentenceMode ? '未削' : '未掌握'}</button>
          </div>
          <div className="wp-pron-btns">
            <button className={`wp-pron-btn ${pronunciationType === 'us' ? 'wp-pron-active' : ''}`} onClick={() => handlePronChange('us')}>美式</button>
            <button className={`wp-pron-btn ${pronunciationType === 'uk' ? 'wp-pron-active' : ''}`} onClick={() => handlePronChange('uk')}>英式</button>
          </div>
          <div className="wp-pron-btns" title="朗读模式">
            <button className={`wp-pron-btn ${playMode === 'en' ? 'wp-pron-active' : ''}`} onClick={() => setPlayMode('en')}>英语</button>
            <button className={`wp-pron-btn ${playMode === 'bilingual' ? 'wp-pron-active' : ''}`} onClick={() => setPlayMode('bilingual')}>双语</button>
          </div>

          </div>
        <div className="wp-playbar-center">
          <div className="wp-sort-btns">
            {isSentenceMode ? (
              <button 
                className={`wp-sort-btn ${sortMode === 'length' ? 'wp-sort-active' : ''}`} 
                onClick={() => {
                  setSortMode('length');
                  setCurrentIndex(0);
                  if (isPlaying) {
                    stopAudio();
                    setTimeout(() => playWordFn(0, playlistRef.current, pronunciationRef.current), 100);
                  }
                }}
                title="按句子长度从短到长播放"
              >
                短→长
              </button>
            ) : (
              <>
                <button 
                  className={`wp-sort-btn ${sortMode === 'asc' ? 'wp-sort-active' : ''}`} 
                  onClick={() => {
                    setSortMode('asc');
                    setCurrentIndex(0);
                    if (isPlaying) {
                      stopAudio();
                      setTimeout(() => playWordFn(0, playlistRef.current, pronunciationRef.current), 100);
                    }
                  }}
                  title="A→Z 顺序播放"
                >
                  A→Z
                </button>
                <button 
                  className={`wp-sort-btn ${sortMode === 'desc' ? 'wp-sort-active' : ''}`} 
                  onClick={() => {
                    setSortMode('desc');
                    setCurrentIndex(0);
                    if (isPlaying) {
                      stopAudio();
                      setTimeout(() => playWordFn(0, playlistRef.current, pronunciationRef.current), 100);
                    }
                  }}
                  title="Z→A 倒序播放"
                >
                  Z→A
                </button>
              </>
            )}
            <button 
              className={`wp-sort-btn ${sortMode === 'random' ? 'wp-sort-active' : ''}`} 
              onClick={() => {
                setSortMode('random');
                setCurrentIndex(0);
                if (isPlaying) {
                  stopAudio();
                  setTimeout(() => playWordFn(0, playlistRef.current, pronunciationRef.current), 100);
                }
              }}
              title="随机顺序播放"
            >
              🔀
            </button>
          </div>
          {!isSentenceMode && sortMode !== 'random' && (
            <select 
              value={startLetter} 
              onChange={(e) => {
                setStartLetter(e.target.value);
                setCurrentIndex(0);
                if (isPlaying) {
                  stopAudio();
                  setTimeout(() => {
                    playWordFn(0, playlistRef.current, pronunciationRef.current);
                  }, 100);
                }
              }} 
              className="wp-letter-select"
              title="从指定字母开始播放"
            >
              <option value="">A起</option>
              {'abcdefghijklmnopqrstuvwxyz'.split('').map(letter => (
                <option key={letter} value={letter}>{letter.toUpperCase()}</option>
              ))}
            </select>
          )}
          <button className="wp-ctrl-btn" onClick={handlePrev} disabled={playlist.length === 0} title="上一个">⏮</button>
          <button className="wp-play-btn" onClick={handlePlayPause} disabled={playlist.length === 0} title={isPlaying ? '暂停' : '播放'}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          <button className="wp-ctrl-btn" onClick={handleNext} disabled={playlist.length === 0} title="下一个">⏭</button>
          <button className={`wp-ctrl-btn wp-loop-btn ${loopMode !== 'none' ? 'wp-loop-active' : ''}`} onClick={handleLoopToggle} title={loopTitle}>
            {loopIcon}
          </button>
        </div>
      </div>

      <div className="wp-divider" />

      {/* 设置面板 */}
      <div className="wp-settings">

        <div className="wp-setting-row">
          <span className="wp-setting-label">字体大小</span>
          <div className="wp-btn-group">
            {fontSizeOptions.map(opt => (
              <button key={opt.value} className={`wp-group-btn ${fontSize === opt.value ? 'wp-group-active' : ''}`} onClick={() => setFontSize(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="wp-setting-row">
          <span className="wp-setting-label">{isSentenceMode ? '每句重复' : '每词重复'}</span>
          <div className="wp-pill-group">
            {[1,2,3,5,10].map(n => (
              <button key={n} className={`wp-pill-btn ${repeatCount === n ? 'wp-pill-active' : ''}`} onClick={() => setRepeatCount(n)}>
                {n}次
              </button>
            ))}
          </div>
        </div>

        <div className="wp-setting-row">
          <span className="wp-setting-label">播放间隔</span>
          <div className="wp-slider-wrap">
            <input
              type="range" min="0.3" max="5" step="0.1"
              value={intervalSec}
              onChange={(e) => setIntervalSec(parseFloat(e.target.value))}
              className="wp-slider"
            />
            <span className="wp-slider-val">{intervalSec.toFixed(1)}s</span>
          </div>
          <span className="wp-slider-hint">0.3 — 5s</span>
          {/* ← 新增还原按钮 */}
          <button
            className="wp-reset-btn"
            onClick={() => setIntervalSec(0.6)}
            title="还原默认间隔"
          >还原</button>
        </div>

        <div className="wp-setting-row">
          <span className="wp-setting-label">定时停止</span>
          <div className="wp-pill-group">
            {timerOptions.map(opt => (
              <button
                key={opt.value}
                className={`wp-pill-btn ${timerMinutes === opt.value ? 'wp-pill-timer-active' : ''}`}
                onClick={() => handleTimerChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="wp-divider" style={{ margin: '4px 0' }} />

      </div>
    </div>
  );
}

export default WordPlayer;
